import * as React from 'react';
import * as Redux from 'react-redux';
import * as Router from 'react-router-dom';
import { Selector } from 'frontend/@types';
import {
  ensureExists,
  getDirName,
  getKeyboardString,
  getPathFileName,
  getPathFileNameNoExt,
  isPathOrDescendant,
  pathJoin,
  setScrollTop,
} from 'frontend/utils';
import { T, $, A, $$ } from 'frontend';
import { FileStoreError } from 'frontend/logic/file-store';
import { persistedState } from 'frontend/logic/persisted-state';

export function useStore(): T.Store {
  return Redux.useStore() as T.Store;
}

export function useDispatch(): T.Dispatch {
  return Redux.useDispatch();
}

export { useSelector } from 'react-redux';

/**
 * Determine at the global app level if a user prefers touch or mouse for UI interaction.
 */
export function useDetectFirstPointerInteraction(): void {
  const dispatch = useDispatch();
  React.useEffect(() => {
    if (persistedState.detectedPointerType.read() !== null) {
      // Already resolved on an earlier visit.
      return undefined;
    }
    function handlePointerDown(event: PointerEvent) {
      dispatch(
        A.setDetectedPointerType(
          event.pointerType === 'touch' ? 'touch' : 'mouse',
        ),
      );
    }
    window.addEventListener('pointerdown', handlePointerDown, {
      capture: true,
      once: true,
    });
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown, {
        capture: true,
      });
    };
  }, [dispatch]);
}

/**
 * Swallows any drag/drop a more specific drop target didn't stopPropagation
 * on, so missing a drop target doesn't navigate the tab to open the file.
 */
export function usePreventUnhandledFileDrop(): void {
  React.useEffect(() => {
    function handleDragOver(event: DragEvent) {
      event.preventDefault();
    }
    function handleDrop(event: DragEvent) {
      event.preventDefault();
    }
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', handleDrop);
    return () => {
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', handleDrop);
    };
  }, []);
}

/**
 * Apply <html data-input-mode="touch"> or <html data-input-mode="mouse"> for CSS rules.
 */
export function useDocumentElementInputMode(): void {
  const resolvedInputMode = $$.getResolvedInputMode();
  React.useEffect(() => {
    document.documentElement.dataset.inputMode = resolvedInputMode;
  }, [resolvedInputMode]);
}

type PromiseState<T> =
  | { type: 'pending' }
  | { type: 'fulfilled'; value: T }
  | { type: 'rejected'; error: unknown };

export function useInfalliblePromise<T>(promise: Promise<T>): null | T {
  const [inner, setInner] = React.useState<T | null>(null);
  React.useEffect(() => {
    promise
      .then((value) => {
        setInner(value);
      })
      .catch((error) => {
        console.error('An assumed infallible promise failed.', error);
      });
  }, [promise]);
  return inner;
}

const pending: PromiseState<any> = {
  type: 'pending',
};

export function usePromise<T>(promise: Promise<T>): PromiseState<T> {
  const [inner, setInner] = React.useState<PromiseState<T>>(pending);
  React.useEffect(() => {
    promise.then(
      (value: T) => {
        setInner({ type: 'fulfilled', value });
      },
      (error: unknown) => {
        setInner({ type: 'rejected', error });
      },
    );
  }, [promise]);
  return inner;
}

export function usePromiseSelector<T>(
  selector: Selector<Promise<T>>,
): PromiseState<T> {
  return usePromise(Redux.useSelector(selector));
}

const scrollTops = new Map<string, number>();

export function useRetainScroll() {
  const navigationType = Router.useNavigationType();

  React.useEffect(() => {
    const { scrollingElement } = document;
    if (scrollingElement === null) {
      return () => {};
    }
    const scrollTop = scrollTops.get(window.location.href);
    setScrollTop(navigationType === 'POP' && scrollTop ? scrollTop : 0);
    const onScroll = () => {
      scrollTops.set(window.location.href, scrollingElement.scrollTop);
    };
    document.addEventListener('scroll', onScroll);
    return () => {
      document.removeEventListener('scroll', onScroll);
    };
  }, [window.location.href, navigationType]);
}

export function useFileDrop(
  targetRef: React.RefObject<HTMLElement | null>,
  onDropCallback: (event: DragEvent) => unknown,
  // Checked on dragenter/dragover/drop. Returning false leaves the event
  // un-prevented, so the browser shows a "not allowed" cursor and never
  // fires `drop` at all, instead of just skipping the drop's side effects.
  canAcceptDrop?: (event: DragEvent) => boolean,
): boolean {
  const [dragging, setDraggingState] = React.useState(false);

  function setDragging(value: boolean) {
    setDraggingState(value);
    if (value) {
      targetRef.current?.classList.add('dragging');
    } else {
      targetRef.current?.classList.remove('dragging');
    }
  }

  React.useEffect(() => {
    const element = targetRef.current;
    if (!element) {
      return () => {};
    }

    const handleDragEnter = (event: DragEvent) => {
      if (canAcceptDrop && !canAcceptDrop(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setDragging(true);
    };

    const handleDragLeave = (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setDragging(false);
    };

    const handleDragOver = (event: DragEvent) => {
      if (canAcceptDrop && !canAcceptDrop(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setDragging(true);
    };

    const handleDrop = (event: DragEvent) => {
      if (canAcceptDrop && !canAcceptDrop(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setDragging(false);
      onDropCallback(event);
    };

    const handleWindowDragEnd = () => {
      // Ensure the dragging is ended.
      setDragging(false);
    };

    const handleWindowDragOver = (event: DragEvent) => {
      if (!(event.target instanceof Node && element.contains(event.target))) {
        setDragging(false);
      }
    };

    element.addEventListener('dragenter', handleDragEnter);
    element.addEventListener('dragover', handleDragOver);
    element.addEventListener('dragleave', handleDragLeave);
    element.addEventListener('drop', handleDrop);
    window.addEventListener('dragend', handleWindowDragEnd);
    window.addEventListener('dragover', handleWindowDragOver, true);

    return () => {
      element.removeEventListener('dragenter', handleDragEnter);
      element.removeEventListener('dragover', handleDragOver);
      element.removeEventListener('dragleave', handleDragLeave);
      element.removeEventListener('drop', handleDrop);
      window.removeEventListener('dragend', handleWindowDragEnd);
      window.removeEventListener('dragover', handleWindowDragOver, true);
    };
  }, [targetRef, onDropCallback, canAcceptDrop]);

  return dragging;
}

type AudioRef = React.MutableRefObject<HTMLAudioElement | null>;
type VideoRef = React.MutableRefObject<HTMLVideoElement | null>;

export function useMedia(
  folderPath: string,
  line:
    | { type: 'audio'; lineIndex: number; src: string; mimetype: string }
    | { type: 'video'; lineIndex: number; src: string; mimetype: string },
  mediaRef: AudioRef | VideoRef,
  getEmptyMediaUrl: () => string,
) {
  const fileStore = Redux.useSelector($.getCurrentFS);
  const [is404, setIs404] = React.useState<boolean>(false);
  const [src, setSrc] = React.useState<string>(getEmptyMediaUrl());
  const srcRef = React.useRef(src);
  const [isPlayRequested, setIsPlayRequested] = React.useState(false);
  const path =
    line.src[0] === '/'
      ? // This is an absolute path.
        line.src
      : // This is a relative path.
        pathJoin(folderPath, line.src);

  // Set the srcRef to a ref so it can be used in event handlers.
  React.useEffect(() => {
    srcRef.current = src;
  }, [src]);

  // When play is requested, download the file from Dropbox and play it.
  React.useEffect(() => {
    if (!path || !isPlayRequested) {
      return () => {};
    }
    let url: string;

    // Download the file from Dropbox.
    void (async () => {
      try {
        let { blob } = await fileStore.loadBlob(path);
        if (!mediaRef.current) {
          return;
        }

        // The file has been downloaded, use it in this component.
        if (blob.type === 'application/octet-stream') {
          // The mimetype was not properly sent.
          blob = blob.slice(0, blob.size, line.mimetype);
        }

        url = URL.createObjectURL(blob);
        mediaRef.current.src = url;
        setSrc(url);
        requestAnimationFrame(() => {
          mediaRef.current?.play().catch(() => {});
        });
      } catch (error) {
        console.error('Media load error:', error);
        setIs404(true);
      }
    })();

    // Clean-up the generated object URL.
    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [fileStore, path, isPlayRequested]);

  function play(
    event: React.SyntheticEvent<HTMLAudioElement | HTMLVideoElement>,
  ) {
    if (mediaRef.current && srcRef.current === getEmptyMediaUrl()) {
      event.preventDefault();
      mediaRef.current.pause();
      setIsPlayRequested(true);
    }
  }

  const isLoaded = src !== getEmptyMediaUrl();

  return { is404, src, isLoaded, path, play };
}

export function useAudio(
  folderPath: string,
  line: { type: 'audio'; lineIndex: number; src: string; mimetype: string },
) {
  const [audio, setAudio] = React.useState<HTMLAudioElement | null>(null);
  const fileStore = Redux.useSelector($.getCurrentFS);
  const [is404, setIs404] = React.useState<boolean>(false);
  const [duration, setDuration] = React.useState<string>('0:00');
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isLoadRequested, setIsLoadRequested] = React.useState(false);
  const isPlayingRef = React.useRef(false);
  const audioRef = propToRef(audio);

  // Let event handlers use the isPlaying value.
  React.useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // Compute these once.
  const { path, name } = React.useMemo(() => {
    const path =
      line.src[0] === '/'
        ? // This is an absolute path.
          line.src
        : // This is a relative path.
          pathJoin(folderPath, line.src);

    const name = getPathFileNameNoExt(line.src)
      .trim()
      // If the track starts with a number, strip it off.
      .replace(/^\d+/, '');

    return { path, name };
  }, []);

  function togglePlay(event: React.SyntheticEvent) {
    const newIsPlayRequested = !isPlayingRef.current;
    setIsPlaying(newIsPlayRequested);
    event.preventDefault();

    // Create the audio element if it's not available.
    let audio = audioRef.current;
    if (!audio) {
      audio = new Audio();
      setAudio(audio);
      console.log('useAudio created an audio element', audio);
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
      });
    }

    if (newIsPlayRequested) {
      // We need to immediately play the audio even if there is no `src`, or else
      // the audio file will not play in Safari. The `play` method must be called
      // within an event handler.
      audio.play().catch(console.error.bind(console));
    } else {
      audio.pause();
    }
  }

  // Determine if a load is requested on the first play.
  React.useEffect(() => {
    if (isPlaying && !isLoadRequested) {
      setIsLoadRequested(true);
    }
  }, [isPlaying]);

  // When play is requested, download the file from Dropbox and play it.
  React.useEffect(() => {
    if (!path || !isLoadRequested || !audio) {
      return () => {};
    }
    let url: string;

    // Download the file from Dropbox.
    void (async () => {
      try {
        let { blob } = await fileStore.loadBlob(path);

        if (blob.type === 'application/octet-stream') {
          // The mimetype was not properly sent.
          blob = blob.slice(0, blob.size, line.mimetype);
        }

        url = URL.createObjectURL(blob);
        audio.src = url;
        audio.addEventListener('loadedmetadata', () => {
          const min = Math.floor(audio.duration / 60);
          const sec = Math.floor(audio.duration % 60);
          setDuration(`${min}:${sec}`);
        });

        if (isPlayingRef.current) {
          audio.play().catch((error) => console.error(error));
        }
      } catch (error) {
        console.error(
          'Media load error:',
          (error as FileStoreError).toString(),
        );
        setIs404(true);
      }
    })();

    // Clean-up the generated object URL.
    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [isLoadRequested]);

  return {
    is404,
    audio,
    path,
    togglePlay,
    isPlaying,
    duration,
    name,
    isLoadRequested,
  };
}

export function useListener<
  E extends HTMLElement | Document,
  Handler extends E['addEventListener'],
  Type extends Parameters<Handler>[0],
  Listener extends Parameters<Handler>[1],
  Options extends Parameters<Handler>[2],
>(
  elementOrRef: E | React.RefObject<E> | null,
  type: Type | Type[],
  deps: any[],
  callback: Listener,
  options?: Options,
): void {
  const types: Type[] = Array.isArray(type) ? type : [type];
  React.useEffect(() => {
    if (!elementOrRef) {
      return () => {};
    }
    const element =
      'current' in elementOrRef ? elementOrRef.current : elementOrRef;
    if (!element) {
      return () => {};
    }
    for (const type of types) {
      element.addEventListener(type, callback, options);
    }

    return () => {
      for (const type of types) {
        element.removeEventListener(type, callback, options);
      }
    };
  }, [elementOrRef, ...deps]);
}

export function useBoundingClientRect(
  element: React.RefObject<HTMLElement | null>,
): DOMRect | null {
  const [rect, setRect] = React.useState<null | DOMRect>(null);

  React.useEffect(() => {
    if (element.current) {
      setRect(element.current.getBoundingClientRect());
    }
  }, [element]);

  useListener(element.current, 'resize', [], (event) => {
    if (event.target) {
      setRect((event.target as HTMLElement).getBoundingClientRect());
    }
  });

  return rect;
}

/**
 * Tracks whether a media query currently matches, re-rendering on change.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState(
    () => window.matchMedia(query).matches,
  );

  React.useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    const onChange = () => setMatches(mediaQueryList.matches);
    onChange();
    mediaQueryList.addEventListener('change', onChange);
    return () => mediaQueryList.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

export function useContext2D(
  canvasRef: React.RefObject<HTMLCanvasElement>,
): null | CanvasRenderingContext2D {
  const [ctx, setContext] = React.useState<null | CanvasRenderingContext2D>(
    null,
  );
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      setContext(ensureExists(canvas.getContext('2d')));
    }
  }, [canvasRef]);
  return ctx;
}

export function propToRef<T>(value: T | null): React.RefObject<T | null> {
  const ref = React.useRef<T | null>(null);
  React.useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}

export function useSelectionChange(
  elementRef: React.RefObject<HTMLElement | null>,
  setSelection: (selection: Selection | null) => void,
  doUseNode: (element: Node | null) => boolean = () => true,
) {
  const selectionRef = React.useRef<Selection | null>(null);

  function getSelectionInDiv() {
    const selection = window.getSelection();
    const div = elementRef.current;
    if (
      selection &&
      div &&
      div.contains(selection.anchorNode) &&
      doUseNode(selection.anchorNode)
    ) {
      return selection;
    }
    return null;
  }

  useListener(document, 'selectionchange', [], () => {
    const selection = getSelectionInDiv();
    if (selection) {
      selectionRef.current = selection;
    }
  });

  useListener(document, 'mouseup', [], () => {
    const selection = selectionRef.current;
    if (!selection) {
      return;
    }
    selectionRef.current = null;

    if (selection.toString()) {
      setSelection(selection);
    } else {
      setSelection(null);
    }
  });

  useListener(document, 'keydown', [], (event) => {
    if ((event as KeyboardEvent).key !== 'Enter') {
      return;
    }
    const selection = getSelectionInDiv();
    if (!selection) {
      return;
    }

    selectionRef.current = null;
    if (selection.toString()) {
      setSelection(selection);
    } else {
      setSelection(null);
    }
  });
}

/**
 * Handle dismissing something by hitting escape.
 */
export function useEscape(
  dismiss: () => void,
  isOpen: boolean,
  exclusive = false,
) {
  const dismissRef = React.useRef(dismiss);
  dismissRef.current = dismiss;
  React.useEffect(() => {
    if (!isOpen) {
      return () => {};
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        if (exclusive) {
          event.stopPropagation();
        }
        dismissRef.current();
      }
    }
    document.addEventListener('keydown', handleKeyDown, exclusive);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, exclusive);
    };
  }, [isOpen, exclusive]);
}

/**
 * Handles "Cmd+Up" (or "Alt+Up") to navigate back out to the containing folder's
 * file listing, only when nothing else (e.g. the CodeMirror editor) is focused.
 */
export function useExitToFolderShortcut() {
  const navigate = Router.useNavigate();
  const dispatch = useDispatch();
  const path = $$.getPath();
  const fsSlug = $$.getCurrentFileStoreSlug();

  React.useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const key = getKeyboardString(event);
      if (key !== 'Meta+ArrowUp' && key !== 'Alt+ArrowUp') {
        return;
      }
      if (document.activeElement !== document.body) {
        // Something else has focus, e.g. an editor is being edited, so
        // don't hijack the shortcut.
        return;
      }
      event.preventDefault();
      const parentPath = getDirName(path);
      const fileName = getPathFileName(path);
      dispatch(A.changeFileFocus(parentPath, fileName));
      dispatch(A.setFileSelection(parentPath, [fileName]));
      navigate(`/${fsSlug}/folder${parentPath}`);
    }
    document.body.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.removeEventListener('keydown', handleKeyDown);
    };
  }, [path, fsSlug, navigate, dispatch]);
}

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function isFocusable(element: HTMLElement) {
  if (element.hasAttribute('disabled')) {
    return false;
  }
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') {
    return false;
  }
  if (element.offsetParent === null && style.position !== 'fixed') {
    return false;
  }
  return true;
}

function getFocusableElements(container: HTMLElement) {
  return Array.from(
    container.querySelectorAll<HTMLElement>(focusableSelector),
  ).filter(isFocusable);
}

/**
 * Traps keyboard focus within a container while it is open, restoring focus to
 * the previously active element on close. Focuses the first focusable child
 * (or the container itself) when opened.
 */
export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
) {
  const priorFocusRef = React.useRef<HTMLElement | null>(null);

  // Save and restore focus around open/close.
  React.useEffect(() => {
    if (!isOpen) {
      return () => {};
    }
    priorFocusRef.current = document.activeElement as HTMLElement | null;
    return () => {
      priorFocusRef.current?.focus();
    };
  }, [isOpen]);

  // Focus first focusable element (or container) when opened.
  React.useEffect(() => {
    if (!isOpen) {
      return;
    }
    const container = containerRef.current;
    if (!container) {
      return;
    }
    requestAnimationFrame(() => {
      const focusable = getFocusableElements(container);
      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        container.focus();
      }
    });
  }, [isOpen]);

  // Cycle Tab/Shift+Tab within the container.
  React.useEffect(() => {
    if (!isOpen) {
      return () => {};
    }
    const container = containerRef.current;
    if (!container) {
      return () => {};
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Match Tab/Shift+Tab exactly so a Ctrl/Cmd/Alt+Tab combo (browser or OS
      // tab-switching) passes through instead of being hijacked by the trap.
      const key = getKeyboardString(event);
      if (key !== 'Tab' && key !== 'Shift+Tab') {
        return;
      }
      const focusable = getFocusableElements(container);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (key === 'Shift+Tab' && active === first) {
        event.preventDefault();
        last.focus();
      } else if (key === 'Tab' && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);
}

import { createPortal } from 'react-dom';

let overlayContainer: HTMLDivElement;

/**
 * Renders into #overlayContainer — the topmost stacking layer, used for context menus,
 * tooltips, and other anchored overlays. Sits above #modalsContainer by DOM order (both
 * z-index: 1; later sibling wins the tie). See Popup.tsx for the component that uses this.
 *
 * Cross-reference: Menus.css (.overlayContainer), Modal.css (.modalsContainer),
 * index.html (DOM order), modalPortal below, Popup.tsx, Modal.tsx.
 * Update all of these if the stacking strategy changes.
 */
export function overlayPortal(child: React.ReactNode, key?: string) {
  if (!overlayContainer || !overlayContainer.isConnected) {
    overlayContainer = ensureExists(
      document.querySelector<HTMLDivElement>('#overlayContainer'),
      'Could not find the overlayContainer',
    );
  }
  return createPortal(child, overlayContainer, key);
}

let modalsContainer: HTMLDivElement;

/**
 * Renders into #modalsContainer — the middle stacking layer, used for full-screen modal
 * dialogs. Sits above app content but below #overlayContainer (context menus) by DOM
 * order (both z-index: 1; later sibling wins the tie). See Modal.tsx for the component
 * that uses this.
 *
 * Cross-reference: Modal.css (.modalsContainer), Menus.css (.overlayContainer),
 * index.html (DOM order), overlayPortal above, Popup.tsx, Modal.tsx.
 * Update all of these if the stacking strategy changes.
 */
export function modalPortal(child: React.ReactNode, key?: string) {
  if (!modalsContainer || !modalsContainer.isConnected) {
    modalsContainer = ensureExists(
      document.querySelector<HTMLDivElement>('#modalsContainer'),
      'Could not find the modalsContainer',
    );
  }
  return createPortal(child, modalsContainer, key);
}

export function useUploadOnFileDrop(
  divRef: React.RefObject<HTMLElement | null>,
  folderPath: string,
) {
  const { getState, dispatch } = useStore();

  function canAcceptDrop() {
    const draggedPaths = $.getDraggedFiles(getState());
    if (!draggedPaths) {
      return true;
    }
    return !draggedPaths.some(
      (draggedPath) =>
        isPathOrDescendant(folderPath, draggedPath) ||
        getDirName(draggedPath) === folderPath,
    );
  }

  useFileDrop(
    divRef,
    (event) => {
      const { dataTransfer } = event;
      if (!dataTransfer) {
        return;
      }

      const draggedPaths = $.getDraggedFiles(getState());
      if (draggedPaths?.length) {
        // A file listing selection was dragged from within the app, move the files instead.
        dispatch(A.clearDraggedFiles());
        if (
          draggedPaths.some(
            (draggedPath) =>
              isPathOrDescendant(folderPath, draggedPath) ||
              getDirName(draggedPath) === folderPath,
          )
        ) {
          throw new Error('You cannot drag files onto their own folder.');
        }
        if (draggedPaths.length > 1) {
          void dispatch(A.moveFiles(draggedPaths, folderPath));
        } else {
          const toPath = pathJoin(folderPath, getPathFileName(draggedPaths[0]));
          void dispatch(A.moveFile(draggedPaths[0], toPath));
        }
        return;
      }

      const { files } = dataTransfer;

      if (files?.length) {
        // This is infallible, as it reports errors with messages.
        void dispatch(
          A.uploadFilesWithMessages(folderPath, dataTransfer.files),
        );
      }
    },
    canAcceptDrop,
  );
}

/**
 * A simple component for displaying an error message.
 */
export function useError() {
  const [errorMessage, setError] = React.useState<null | string>(null);

  let error = null;
  if (errorMessage) {
    error = (
      <div className="error">
        {errorMessage}
        <button
          type="button"
          className="errorDismiss"
          onClick={() => {
            setError(null);
          }}
        >
          &times;
        </button>
      </div>
    );
  }
  return { error, setError };
}

/**
 * Determine if a file is cached or not in the local IndexedDB file cache.
 */
export function useIsFileCached(
  file: T.FileMetadata | T.FolderMetadata,
): boolean {
  const fileStore = $$.getCurrentFSOrNull();
  const [isCached, setIsCached] = React.useState(false);

  // Track if the component is already destroyed, or else the cache lookup promise may
  // fire after the component has already been unmounted.
  const isDestroyed = React.useRef(false);

  React.useEffect(() => {
    if (file.type === 'folder' || !fileStore) {
      setIsCached(false);
      return undefined;
    }
    const { cache } = fileStore;
    if (!cache) {
      setIsCached(false);
      return undefined;
    }

    cache.isCached(file.path).then(
      (isCached) => {
        if (!isDestroyed.current) {
          setIsCached(Boolean(isCached));
        }
      },
      () => {
        if (!isDestroyed.current) {
          setIsCached(false);
        }
      },
    );
    return () => {
      isDestroyed.current = true;
    };
  }, [fileStore, file]);

  return isCached;
}

const _emptySet = new Set<string>();

/**
 * Determine if a file is cached or not in the local IndexedDB file cache.
 */
export function useCachedFolderListing(): Set<string> {
  const path = $$.getPath();
  const folderListing = $$.getListFilesCache().get(path)?.files;
  const fileStore = $$.getCurrentFSOrNull();
  const [cachedFolderListing, setCached] =
    React.useState<Set<string>>(_emptySet);

  // Track if the component is already destroyed, or else the cache lookup promise may
  // fire after the component has already been unmounted.
  const isDestroyed = React.useRef(false);

  React.useEffect(() => {
    if (!fileStore?.cache || !folderListing) {
      setCached(_emptySet);
      return undefined;
    }
    fileStore.cache.getCachedFolderListing(folderListing).then(
      (cachedListing) => {
        if (!isDestroyed.current) {
          setCached(cachedListing);
        }
      },
      () => {
        if (!isDestroyed.current) {
          setCached(_emptySet);
        }
      },
    );
    return () => {
      isDestroyed.current = true;
    };
  }, [fileStore, folderListing]);

  return cachedFolderListing;
}

/**
 * Jumps to the first item whose text starts with the characters typed in
 * quick succession. Resets the accumulated query after a pause.
 */
export function useTypeAheadSearch(
  listRef: React.RefObject<HTMLElement | null>,
  items: string[],
  onSelect: (value: string) => void,
): void {
  const itemsRef = React.useRef(items);
  itemsRef.current = items;

  const onSelectRef = React.useRef(onSelect);
  onSelectRef.current = onSelect;

  React.useEffect(() => {
    const queryRef = { current: '' };
    let timer: ReturnType<typeof setTimeout> | null = null;

    function handleKeyDown(event: KeyboardEvent) {
      if (document.activeElement !== listRef.current) {
        return;
      }
      /* eslint-disable indie-web/prefer-keyboard-string */
      if (
        event.key.length !== 1 ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey
      ) {
        return;
      }
      /* eslint-enable indie-web/prefer-keyboard-string */

      if (timer !== null) {
        clearTimeout(timer);
      }

      queryRef.current += event.key.toLowerCase();
      timer = setTimeout(() => {
        queryRef.current = '';
        timer = null;
      }, 1000);

      const query = queryRef.current;
      const match = itemsRef.current.find((item) =>
        item.toLowerCase().startsWith(query),
      );
      if (match) {
        onSelectRef.current(match);
      }
    }

    document.body.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.removeEventListener('keydown', handleKeyDown);
      if (timer !== null) {
        clearTimeout(timer);
      }
    };
  }, []);
}
